import { useTranslation } from 'react-i18next';

import { ActiveBet } from '../active-bet';
import { MoveCards } from '../move-cards';
import { PlayerCard } from '../player-card';
import { RoomBox } from '../room-box';
import { RoundCounter } from '../round-counter';
import { Score } from '../score';
import { StreamRoomFooter } from '../stream-room-footer';

import MobileRoomLayout from 'shared/components/layout/mobile-layout';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { Spinner } from 'shared/components/ui/Loaders/spinner/spinner';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum, WinnerStatusEnum } from 'shared/constants/shared-enums';
import { CurrencyType } from 'shared/web3';

import { useStreamRoom } from './use-stream-room';
import PixiComponent from '@/pixi/PixiComponent';

export interface IStreamRoomMobileProps {
  isGuestMode: boolean;
  streamCurrency?: CurrencyType | undefined;
}

export const StreamRoomMobile = ({ isGuestMode, streamCurrency }: IStreamRoomMobileProps) => {
  const { t } = useTranslation();

  const {
    isInitialLoading,
    is404,
    playerAWonRound,
    playerBWonRound,
    roundDraw,
    playerASelectedMove,
    playerBSelectedMove,
    playerAWins,
    playerBWins,
    renderEndRoundPhrase,
    currentPhase,
    currentRound,
    queue,
    room,
    roomDescription,
  } = useStreamRoom({ isGuestMode });

  const isGameFinished = queue?.length === 0 && room.roomStatus === RoomStatusEnum.Closed;
  const isRoundFinished = playerAWonRound || playerBWonRound || roundDraw;

  if (isInitialLoading) {
    return (
      <MobileRoomLayout
        title={
          room.roomStatus !== RoomStatusEnum.None && (
            <>
              {room.roomStatus === RoomStatusEnum.Closed
                ? t('modals.watchGame.gameReplay')
                : t('modals.watchGame.liveGame')}
              {room.roomStatus !== RoomStatusEnum.Closed && (
                <div className="h-[16px] w-[16px] animate-pulse rounded-[50%] bg-[#0AF33D]" />
              )}
            </>
          )
        }
        footer={<StreamRoomFooter currentRound={currentRound} isGameFinished={isGameFinished} />}
        footerClassNames="sm:h-[unset] bg-[linear-gradient(to_bottom,rgba(8,5,64,0)_0%,#080540_25%,#080540_100%)]"
      >
        <div className="flex h-full w-full flex-grow items-center justify-center">
          <Spinner />
        </div>
      </MobileRoomLayout>
    );
  }

  if (is404) {
    return (
      <MobileRoomLayout
        title={
          room.roomStatus !== RoomStatusEnum.None && (
            <>
              {room.roomStatus === RoomStatusEnum.Closed
                ? t('modals.watchGame.gameReplay')
                : t('modals.watchGame.liveGame')}
              {room.roomStatus !== RoomStatusEnum.Closed && (
                <div className="h-[16px] w-[16px] animate-pulse rounded-[50%] bg-[#0AF33D]" />
              )}
            </>
          )
        }
        footer={<StreamRoomFooter currentRound={currentRound} isGameFinished={isGameFinished} />}
        footerClassNames="sm:h-[unset] bg-[linear-gradient(to_bottom,rgba(8,5,64,0)_0%,#080540_25%,#080540_100%)]"
      >
        <div className="flex h-full w-full flex-grow items-center justify-center">
          <div className="mb-[96px] text-8xl font-bold text-white">404</div>
        </div>
      </MobileRoomLayout>
    );
  }

  return (
    <MobileRoomLayout
      title={
        room.roomStatus !== RoomStatusEnum.None && (
          <>
            {room.roomStatus === RoomStatusEnum.Closed
              ? t('modals.watchGame.gameReplay')
              : t('modals.watchGame.liveGame')}
            {room.roomStatus !== RoomStatusEnum.Closed && (
              <div className="h-[16px] w-[16px] animate-pulse rounded-[50%] bg-[#0AF33D]" />
            )}
          </>
        )
      }
      footer={
        <StreamRoomFooter
          currentRound={currentRound}
          isGameFinished={isGameFinished}
          roomDescription={roomDescription}
        />
      }
      footerClassNames="sm:h-[unset] w-full bg-[linear-gradient(to_bottom,rgba(8,5,64,0)_0%,#080540_25%,#080540_100%)]"
      className="overflow-y-hidden"
    >
      <div className="absolute left-0 z-10 mb-20 mt-[100px] flex h-[80%] w-full flex-col md:h-[89%]">
        <PixiComponent />
      </div>

      <div className="relative flex h-full max-h-[1080px] w-full max-w-screen-hd flex-row items-stretch justify-center">
        <div className="relative w-full">
          <div className="relative mx-auto flex h-fit max-h-[1080px] max-w-screen-hd animate-appear-with-blur flex-row items-stretch justify-between gap-1.5 rounded-xl border border-border bg-background bg-opacity-30 p-1.5 transition sm:p-5 md:w-full lg:h-full lg:min-h-[800px] lg:rounded-3xl lg:border-none lg:bg-transparent lg:p-0 lg:py-4">
            <div className="z-20 order-1 flex h-fit animate-appear-with-blur justify-center sm:max-h-[890px] sm:max-w-[630px] lg:mt-8 lg:w-full">
              <PlayerCard
                position="order-1"
                name="Player 1"
                wins={playerBWins}
                playerAddress={room.playerA.address}
                isWinner={
                  isGameFinished &&
                  (room.winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
                    room.winnerStatus === WinnerStatusEnum.TechnicalWinnerA)
                }
                isLooser={
                  isGameFinished &&
                  (room.winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
                    room.winnerStatus === WinnerStatusEnum.TechnicalWinnerB)
                }
                winsTo={room.winsTo}
              />
            </div>
            <div className="relative z-30 order-2 flex h-fit w-1/2 animate-appear flex-col items-center gap-0.5 xs:w-auto sm:gap-1">
              <Score playerAWins={playerAWins} playerBWins={playerBWins} />

              <RoundCounter currentRound={currentRound} />

              <ActiveBet
                bet={room.bet}
                token={room.token}
                animationsTriggered={true}
                streamCurrency={isGuestMode ? streamCurrency : undefined}
              />

              <AnimatedTransition show={currentPhase === 'done' || isGameFinished} className="z-10">
                <>{renderEndRoundPhrase()}</>
              </AnimatedTransition>
            </div>
            <div className="z-20 order-3 flex h-fit animate-appear-with-blur justify-center sm:max-h-[890px] sm:max-w-[630px] lg:mt-8 lg:w-full">
              {room.playerB.address !== NULL_ADDRESS ? (
                <PlayerCard
                  position="order-3"
                  name="Player 2"
                  wins={playerAWins}
                  playerAddress={room.playerB.address}
                  isWinner={
                    isGameFinished &&
                    (room.winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
                      room.winnerStatus === WinnerStatusEnum.TechnicalWinnerB)
                  }
                  isLooser={
                    isGameFinished &&
                    (room.winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
                      room.winnerStatus === WinnerStatusEnum.TechnicalWinnerA)
                  }
                  winsTo={room.winsTo}
                />
              ) : null}
            </div>
          </div>
        </div>

        <div
          className={`absolute left-10 top-[200px] z-40 h-[75px] w-[50px] transition-all duration-500 ease-in-out ${isRoundFinished ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-4 opacity-0'} sm:left-[120px] sm:top-[280px] lg:left-[300px] lg:top-[340px]`}
        >
          <MoveCards
            position="left"
            isRoundFinished={isRoundFinished}
            selectedMove={playerASelectedMove}
            isWinner={playerAWonRound}
            roundDraw={roundDraw}
            currentPhase={currentPhase}
            isGameFinished={isGameFinished}
          />
        </div>

        <div
          className={`absolute right-[40px] top-[200px] z-40 h-[75px] w-[50px] transition-all duration-500 ease-in-out ${isRoundFinished ? 'translate-x-0 opacity-100' : 'pointer-events-none translate-x-4 opacity-0'} sm:right-[160px] sm:top-[280px] lg:right-[330px] lg:top-[340px]`}
        >
          <MoveCards
            position="right"
            isRoundFinished={isRoundFinished}
            selectedMove={playerBSelectedMove}
            isWinner={playerBWonRound}
            roundDraw={roundDraw}
            currentPhase={currentPhase}
            isGameFinished={isGameFinished}
          />
        </div>
      </div>

      {room.bet ? (
        <RoomBox
          token={room.token}
          bet={room.bet ? room.bet * 2 : null}
          animationsTriggered={true}
          streamCurrency={isGuestMode ? streamCurrency : undefined}
        />
      ) : null}
    </MobileRoomLayout>
  );
};
